/**
 * Telegram WebApp start-param decoder for Shieldz Payments deep links.
 *
 * When a buyer taps "Pay with Shieldz" on the hosted checkout page, the
 * browser opens `https://t.me/shieldz_wallet_bot?startapp=pay_<public_id>`.
 * Telegram loads the WebApp with `initDataUnsafe.start_param = "pay_<id>"`.
 *
 * This module is the single place we parse those params. Keeping it
 * self-contained means the App.tsx routing logic doesn't need to know
 * the prefix conventions, and we can add new deep-link types later
 * (e.g. `inv_<id>` for invoice-details, `ref_<code>` for referrals)
 * without touching the route shape.
 */

export type StartParam =
  | { kind: "pay_invoice"; invoicePublicId: string }
  | { kind: "unknown"; raw: string }
  | null;

interface TelegramWebAppLike {
  initDataUnsafe?: { start_param?: string };
}

/**
 * Returns the parsed start-param from the live Telegram WebApp object, or
 * null if we're not inside Telegram / no start-param was given.
 */
export const readStartParam = (): StartParam => {
  if (typeof window === "undefined") return null;
  const w = window as unknown as { Telegram?: { WebApp?: TelegramWebAppLike } };
  const raw = w.Telegram?.WebApp?.initDataUnsafe?.start_param;
  if (typeof raw !== "string" || raw.length === 0) return null;
  return parseStartParam(raw);
};

/**
 * Pure parser. Exported for tests and for the rare case where we want to
 * accept a param from a non-Telegram source (e.g. URL query for dev).
 */
export const parseStartParam = (raw: string): StartParam => {
  if (raw.startsWith("pay_")) {
    const id = raw.slice("pay_".length);
    // public_ids are base64url, 18-30 chars. Be tolerant of trailing slashes
    // or whitespace from some Telegram clients.
    const cleaned = id.replace(/[^A-Za-z0-9_-]/g, "");
    if (cleaned.length >= 4 && cleaned.length <= 40) {
      return { kind: "pay_invoice", invoicePublicId: cleaned };
    }
  }
  return { kind: "unknown", raw };
};
